import {
  type Location,
  Navigate,
  type NonIndexRouteObject,
  Outlet,
  ScrollRestoration
} from "react-router-dom"

import { ExistingDocument, NewDocument } from "../../document"
import { DocumentList } from "../../documents/DocumentList"
import { MigrationReleasePage } from "../../documents/migrationRelease/MigrationReleasePage"
import { ReleasePage } from "../../documents/releases/ReleasePage"
import { ReleasesList } from "../../documents/releases/ReleasesList"
import { Lab } from "../../lab"
import { MediaLibraryPage } from "../../medias/MediaLibraryPage"
import { LocalesPage } from "../../settings/LocalesPage"
import { RouterError } from "../components/AppErrorHandler"
import { E2eReminder } from "../components/E2EReminder"
import { SuperUserReminder } from "../components/SuperUserReminder"
import { isLinkState } from "./Link"
import { RouteStateProvider } from "./RouteStateContext"

type RouteId =
  | "index"
  | "existingDocument"
  | "newDocument"
  | "medias"
  | "working"
  | "archived"
  | "upcoming"
  | "upcoming-detail"
  | "migration"
  | "locales"
  | "lab"

export type PageBuilderRoute = NonIndexRouteObject & { id: RouteId }

export const routes = [
  {
    path: "/",
    element: <RootRoute />,
    errorElement: <RouterError />,
    children: [
      {
        id: "index",
        path: "/",
        element: <Navigate to="working" replace />
      },
      {
        id: "working",
        path: "working",
        element: <DocumentList tab="working" />
      },
      {
        id: "upcoming",
        path: "upcoming",
        element: <ReleasesList />
      },
      {
        id: "upcoming-detail",
        path: "upcoming/:releaseId",
        element: <ReleasePage />
      },
      {
        id: "archived",
        path: "archived",
        element: <DocumentList tab="archived" />
      },
      {
        id: "migration",
        path: "migration",
        element: <MigrationReleasePage />
      },
      {
        id: "medias",
        path: "medias",
        element: <MediaLibraryPage />
      },
      {
        id: "existingDocument",
        path: "pages/:documentId",
        element: <ExistingDocument />
      },
      {
        id: "newDocument",
        path: "pages/new",
        element: <NewDocument />
      },
      {
        id: "locales",
        path: "settings/locales",
        element: <LocalesPage />
      },
      {
        id: "lab",
        path: "lab",
        element: <Lab />
      }
    ] satisfies PageBuilderRoute[]
  }
]

// Pure shared logic parent of all other routes.
// This shouldn't add any default/shared view/chrome, App can do that.
function RootRoute() {
  return (
    <>
      <SuperUserReminder />
      <E2eReminder />
      <ScrollRestoration getKey={getScrollRestorationKey} />
      <RouteStateProvider>
        <Outlet />
      </RouteStateProvider>
    </>
  )
}

// The default getKey is location.key (https://reactrouter.com/en/main/components/scroll-restoration#getkey)
// This doesn't work well with our Document list back button in particular as it's considered a new location (we're not merely calling history.back).
function getScrollRestorationKey(location: Location<unknown>) {
  if (isLinkState(location.state) && location.state.scrollToTop)
    return location.key

  return location.pathname
}
